import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import { LogOut, LayoutDashboard, GitBranch, Calendar, Wallet, MoreHorizontal, Users, FileText, TrendingUp, Settings } from "lucide-react";

const PRIMARY = [
  { to: "/commercial", label: "Suivi", Icon: LayoutDashboard, exact: true },
  { to: "/commercial/opportunites", label: "Opportunités", Icon: GitBranch },
  { to: "/commercial/rendez-vous", label: "Rendez-vous", Icon: Calendar },
  { to: "/commercial/commissions", label: "Commissions", Icon: Wallet },
];

const SECONDARY = [
  { to: "/commercial/clients", label: "Clients", Icon: Users },
  { to: "/commercial/contrats", label: "Contrats", Icon: FileText },
  { to: "/commercial/statistiques", label: "Statistiques détaillées", Icon: TrendingUp },
];

export default function CommercialHeader() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const moreActive = SECONDARY.some((n) => pathname.startsWith(n.to));

  return (
    <header className="border-b border-brand-line bg-white sticky top-0 z-30">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-10 h-14 flex items-center gap-4 lg:gap-6">
        <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0" data-testid="commercial-brand">
          <img src="/picto-instant.png" alt="Agence Instant" className="h-7 w-7" />
          <div className="hidden md:block leading-none">
            <div className="font-display text-sm text-brand-ink">Instant Studio</div>
            <div className="text-[10px] text-brand-subtle uppercase tracking-widest mt-0.5">Suivi commercial</div>
          </div>
        </Link>
        <div className="h-6 w-px bg-brand-line hidden md:block flex-shrink-0" />
        <nav className="flex items-center gap-0.5 overflow-x-auto flex-1 min-w-0">
          {PRIMARY.map(({ to, label, Icon, exact }) => {
            const active = exact ? pathname === to : pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                data-testid={`nav-${to.replace(/\//g, "-").replace(/^-/, "")}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors ${
                  active ? "bg-brand-ink text-white" : "text-brand-ink/80 hover:bg-brand-paper hover:text-brand-ink"
                }`}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                <span className="hidden sm:inline">{label}</span>
              </Link>
            );
          })}
          <div ref={ref} className="relative">
            <button
              onClick={() => setMoreOpen((o) => !o)}
              data-testid="nav-more"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors ${
                moreActive ? "bg-brand-ink text-white" : "text-brand-ink/80 hover:bg-brand-paper hover:text-brand-ink"
              }`}
            >
              <MoreHorizontal className="h-3.5 w-3.5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Plus</span>
            </button>
            {moreOpen && (
              <div className="absolute right-0 mt-1 w-56 rounded-md border border-brand-line bg-white shadow-card py-1 z-40" data-testid="nav-more-menu">
                {SECONDARY.map(({ to, label, Icon }) => (
                  <Link
                    key={to} to={to}
                    onClick={() => setMoreOpen(false)}
                    data-testid={`nav-more-${to.replace(/\//g, "-").replace(/^-/, "")}`}
                    className={`flex items-center gap-2 px-3 py-2 text-xs ${
                      pathname.startsWith(to) ? "bg-brand-paper text-brand-ink" : "text-brand-ink/80 hover:bg-brand-paper"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5 text-brand-moss" strokeWidth={1.5} />
                    {label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>
        {user && typeof user === "object" && (
          <button
            onClick={logout}
            data-testid="commercial-logout"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs text-brand-subtle hover:text-brand-ink flex-shrink-0"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
            <span className="hidden lg:inline">Déconnexion</span>
          </button>
        )}
      </div>
    </header>
  );
}
